"use client";

import { Box } from "@mui/system";
import React from "react";
import { Hero } from "@/types/dto/hero/hero";
import { BasicInfo } from "@/types/dto/common/basic-info";
import { MediaCard, MediaCardSection } from "@/types/dto/common/media-card-section";
import { InterlinkSection } from "@/types/dto/common/common-interlinking";
import TextModule from "@/components/common/text-module/text-module";
import MediaTextCard from "@/components/common/media-text-card/media-text-card";
import BasePageHero from "@/components/common/page-hero/base-page-hero/base-page-hero";
import CommonInterLinking from "@/components/common/common-interlinking";
import OurValuesTextCard from "./our-values-text-card";
import LegacyCard from "../legacy-card-section/legacy-card-section";

interface OurValuesPageLayoutProps {
  heroSection: Hero;
  copyBlock: BasicInfo;
  coreValues: MediaCardSection;
  legacyCard: MediaCard;
  interlinkSection: InterlinkSection[];
}

const OurValuesPageLayout = ({
  heroSection,
  copyBlock,
  coreValues,
  legacyCard,
  interlinkSection
}: OurValuesPageLayoutProps) => (
  <Box>
    {/* heroSection */}
    {heroSection && heroSection.backgroundImage?.imageUrl && (
      <BasePageHero
        pageTitle={heroSection.basicInfo.headerText ?? ""}
        imageSrc={heroSection.backgroundImage.imageUrl}
        imageAlt={heroSection.backgroundImage.altText ?? "Our Values Hero Image"}
        dataModuleName="Page Hero"
        moduleRankingPosition="1"
        dataParentContainerType="our-values-page-hero"
      />
    )}

    {copyBlock && (
      <TextModule
        basicInfo={{
          copyText: copyBlock.copyText
        }}
        moduleName="copy_block"
        moduleRankingPosition={2}
        moduleParentContainerType="our-values-copy-block"
        slotProps={{
          root: {
            sx: {
              display: "flex",
              justifyContent: "center",
              padding: { xs: "1.4rem 2.4rem", md: "4.2rem 10rem", lg: "4.2rem 10rem" },
              bgcolor: " background.warm_strong"
            }
          },
          copy: {
            testId: "our-values-subheader",
            sx: {
              "& p": { marginY: "1.8rem" },
              color: "text.primary"
            }
          }
        }}
      />
    )}

    {/* textModule */}
    {coreValues?.basicInfo && (
      <TextModule
        basicInfo={coreValues?.basicInfo}
        moduleRankingPosition={3}
        slotProps={{
          root: {
            className: "pattern-bg"
          }
        }}
      />
    )}

    {/* mediacard */}
    {coreValues?.mediaCards?.length > 0 && (
      <Box
        component="section"
        data-track="visibility"
        data-module-name="core-values-section"
        data-module-ranking-position="4"
        data-parent-container-type="core-values-section"
        sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}
      >
        {coreValues?.mediaCards?.map((card: MediaCard, index: number) =>
           (
            <MediaTextCard
              key={card?.mediaCardId || index}
              index={index}
              reverseDesktop={card?.design === "Text Left"}
              card={card}
              bgImage={index === 1 ? "/images/world-map.svg" : undefined}
              testId={index}
            >
              <OurValuesTextCard
                headerText={card.overlayText?.headerText}
                copyText={card.overlayText?.copyText}
                subtitle={card.overlayText?.subtitle}
                index={index}
              />
            </MediaTextCard>
          )
        )}
      </Box>
    )}

    {legacyCard && (
      <LegacyCard
        cardData={legacyCard}
        dataParentContainerType="our-values-legacy-card"
        moduleRankingPosition={5}
      />
    )}

    {/* interlinking section */}
    {interlinkSection && (
      <CommonInterLinking interlinkData={interlinkSection} dataModuleRanking={5} />
    )}
  </Box>
);

export default OurValuesPageLayout;
